'use client';

import { Badge, cn } from '@up2stay/up2promo-ui';
import { AlertCircle, Link2, Star, type LucideIcon } from 'lucide-react';

import type { LocationConnectionStatus } from '@/core/lib/review-channel-setup';

import type {
  LocationHealthSummary,
  LocationLinksHealth,
} from './location-health';

export type LocationHealthLabels = {
  googleLinked: string;
  /** Listing ID set without active review sync — not success-green. */
  googleListing: string;
  googleNeedsAction: string;
  googleOptOut: string;
  googleSyncFailed: string;
  linksOk: string;
  linksEmpty: string;
  /** Short channel label on the badge (e.g. "Reviews" / "Bewertungen"). */
  googleChannel: string;
  linksChannel: string;
  statusColumn?: string;
  ariaGroup?: string;
};

function googleLabel(
  status: LocationConnectionStatus,
  labels: LocationHealthLabels,
): string {
  switch (status) {
    case 'linked':
      return labels.googleLinked;
    case 'listing':
      return labels.googleListing;
    case 'opt_out':
      return labels.googleOptOut;
    default:
      return labels.googleNeedsAction;
  }
}

function googleBadgeVariant(
  status: LocationConnectionStatus,
): 'destructive' | 'secondary' | 'outline' | 'success' | 'warning' {
  switch (status) {
    case 'linked':
      return 'success';
    case 'listing':
      // Place ID alone is not ops-healthy — attention, not Signal Red.
      return 'warning';
    case 'needs_action':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function linksBadgeVariant(
  status: LocationLinksHealth,
): 'destructive' | 'success' {
  // Empty links are actionable for guest redirects — Signal Red interrupt.
  return status === 'ok' ? 'success' : 'destructive';
}

function HealthChannelBadge({
  icon: Icon,
  channelLabel,
  statusLabel,
  variant,
}: {
  icon: LucideIcon;
  channelLabel: string;
  statusLabel: string;
  variant: 'destructive' | 'secondary' | 'outline' | 'success' | 'warning';
}) {
  const fullLabel = `${channelLabel}: ${statusLabel}`;
  return (
    <Badge
      variant={variant}
      className="h-6 gap-1 px-2 py-0 font-medium"
      aria-label={fullLabel}
      title={fullLabel}
    >
      <Icon className="h-3 w-3 shrink-0 opacity-80" aria-hidden />
      <span>{statusLabel}</span>
    </Badge>
  );
}

/**
 * Health chips for list cards — Status Board scan with status on the face
 * (channel via icon + aria). Mobile-safe: no hover required.
 */
export function LocationHealthDots({
  health,
  labels,
  className,
  includeReputation = true,
}: {
  health: LocationHealthSummary;
  labels: LocationHealthLabels;
  className?: string;
  includeReputation?: boolean;
}) {
  return (
    <div
      className={cn('flex flex-wrap items-center gap-1.5', className)}
      role="group"
      aria-label={labels.ariaGroup ?? labels.statusColumn}
    >
      {includeReputation ? (
        health.syncFailed ? (
          <HealthChannelBadge
            icon={AlertCircle}
            channelLabel={labels.googleChannel}
            statusLabel={labels.googleSyncFailed}
            variant="destructive"
          />
        ) : (
          <HealthChannelBadge
            icon={Star}
            channelLabel={labels.googleChannel}
            statusLabel={googleLabel(health.google, labels)}
            variant={googleBadgeVariant(health.google)}
          />
        )
      ) : null}
      <HealthChannelBadge
        icon={Link2}
        channelLabel={labels.linksChannel}
        statusLabel={health.links === 'ok' ? labels.linksOk : labels.linksEmpty}
        variant={linksBadgeVariant(health.links)}
      />
    </div>
  );
}

export function GoogleConnectionBadge({
  status,
  labels,
  className,
}: {
  status: LocationConnectionStatus;
  labels: Pick<
    LocationHealthLabels,
    'googleLinked' | 'googleListing' | 'googleNeedsAction' | 'googleOptOut'
  >;
  className?: string;
}) {
  if (status === 'needs_action') {
    return (
      <Badge variant="destructive" className={className}>
        {labels.googleNeedsAction}
      </Badge>
    );
  }
  if (status === 'linked') {
    return (
      <Badge variant="success" className={className}>
        {labels.googleLinked}
      </Badge>
    );
  }
  if (status === 'listing') {
    return (
      <Badge variant="warning" className={className}>
        {labels.googleListing}
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className={className}>
      {labels.googleOptOut}
    </Badge>
  );
}
